import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Chart } from 'react-google-charts';
import { ThemeContext } from 'styled-components';
import { logError } from '../../utils/tools/ErrorLoggers';
import { barChartOptions } from './BarChartOptions';
import { BarChartProps, ChartClickEvent, ChartController, ChartListenerHandle, GoogleEventsApi } from './types/BarChart.types';
import {
    getBarChartColumns,
    getBarChartRows,
    normalizeTargetId,
    scheduleAnnotationStyling,
    scheduleXAxisLabelSlant,
    setHoverInteraction,
} from './utils/BarChart.utils';

export default function BarChart(props: BarChartProps) {
    const [chartKey, setChartKey] = useState(Math.random());
    const { data, toUserChart, hTitle, chartColors, selectedChart, isDrillDownAllowed } = props;
    const theme = useContext(ThemeContext) as { primaryMain?: string } | undefined;
    const annotationColor = theme?.primaryMain;
    const listenersRef = useRef<ChartListenerHandle[]>([]);
    const eventsApiRef = useRef<GoogleEventsApi | null>(null);
    const annotationStylingCleanupRef = useRef<(() => void) | null>(null);
    const options = useMemo(
        () => barChartOptions(hTitle, chartColors, selectedChart, data, isDrillDownAllowed, annotationColor),
        [hTitle, chartColors, selectedChart, data, isDrillDownAllowed, annotationColor],
    );

    useEffect(() => setChartKey(Math.random()), [data]);

    const cleanupChartResources = useCallback(() => {
        annotationStylingCleanupRef.current?.();
        annotationStylingCleanupRef.current = null;

        listenersRef.current.forEach(listener => {
            listener?.remove?.();
        });
        listenersRef.current = [];
    }, []);

    useEffect(
        () => () => {
            cleanupChartResources();
        },
        [chartKey, cleanupChartResources],
    );

    return (
        <Chart
            chartType="ColumnChart"
            height="600px"
            rows={getBarChartRows(data)}
            columns={getBarChartColumns(data)}
            options={options}
            key={chartKey}
            chartEvents={[
                {
                    eventName: 'ready',
                    callback: ({ chartWrapper, google }) => {
                        cleanupChartResources();
                        const chart = chartWrapper.getChart() as ChartController;
                        let cleanupAnnotationStyling = () => {};
                        let cleanupXAxisLabelSlant = () => {};
                        try {
                            cleanupAnnotationStyling = scheduleAnnotationStyling(chartWrapper, annotationColor);
                            cleanupXAxisLabelSlant = scheduleXAxisLabelSlant(chartWrapper);
                        } catch (error) {
                            logError('Error scheduling chart text styling', error);
                        }
                        annotationStylingCleanupRef.current = () => {
                            try {
                                cleanupAnnotationStyling();
                            } catch (error) {
                                logError('Error cleaning annotation styling', error);
                            }
                            try {
                                cleanupXAxisLabelSlant();
                            } catch (error) {
                                logError('Error cleaning x-axis label slant', error);
                            }
                        };
                        const eventsApi = google.visualization.events as GoogleEventsApi;
                        eventsApiRef.current = eventsApi;
                        const handler = function (e: ChartClickEvent) {
                            const selection = chart.getSelection?.() || [];
                            const selectedPoint = selection?.[0];
                            const normalizedTargetID = normalizeTargetId(e?.targetID, selectedPoint);

                            toUserChart({
                                ...e,
                                targetID: normalizedTargetID,
                            });
                        };
                        listenersRef.current.push(
                            eventsApi.addListener(chart, 'click', handler),
                            eventsApi.addListener(chart, 'onmouseover', (e: any) =>
                                setHoverInteraction(chartWrapper, chart, e, annotationColor),
                            ),
                            eventsApi.addListener(chart, 'onmouseout', () => {
                                chart?.setSelection?.([]);
                                setHoverInteraction(chartWrapper, chart, {}, annotationColor);
                            }),
                        );
                    },
                },
            ]}
        />
    );
}
